import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Heart, TreeDeciduous, FolderLock, ListChecks, Siren, Users, Settings, LogOut, Sparkles, ShieldCheck } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { usePresentation } from "@/lib/presentation";
import { Button } from "@/components/ui/button";
import AppFooter from "@/components/AppFooter";
import Logo from "@/components/Logo";

const links = [
  { to: "/dashboard", icon: Heart, key: "myLife", testId: "nav-mylife" },
  { to: "/lebensbaum", icon: TreeDeciduous, key: "lifeTree", testId: "nav-lifetree" },
  { to: "/vault", icon: FolderLock, key: "vault", testId: "nav-vault" },
  { to: "/aufgaben", icon: ListChecks, key: "tasks", testId: "nav-tasks" },
  { to: "/notfall", icon: Siren, key: "emergency", testId: "nav-emergency" },
  { to: "/vertrauenskarte", icon: ShieldCheck, key: "trustCard", testId: "nav-trustcard" },
  { to: "/familie", icon: Users, key: "family", testId: "nav-family" },
  { to: "/einstellungen", icon: Settings, key: "settings", testId: "nav-settings" },
];

export default function AppLayout({ children }) {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const { presenting, setPresenting } = usePresentation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-ag-paper ag-paper-grain">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex ag-sidebar w-64 flex-col text-slate-100 fixed inset-y-0 left-0 z-30">
        <div className="px-7 pt-10 pb-8 border-b border-white/5">
          <Logo variant="full" tone="dark" className="h-[78px] w-auto -ml-1" />
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1">
          {links.map(({ to, icon: Icon, key, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                  isActive
                    ? "bg-white/10 text-white font-medium"
                    : "text-slate-300 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
              <span>{t.nav[key]}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-4 pb-6 space-y-3">
          {user?.is_demo && (
            <div className="px-3 py-2 rounded-xl border border-amber-300/30 bg-amber-300/10 text-amber-200 text-xs">
              <div className="flex items-center gap-1.5 font-medium"><Sparkles className="h-3 w-3" /> Demo-Modus</div>
              <div className="text-amber-200/80 mt-0.5">{user.first_name} {user.last_name}</div>
            </div>
          )}
          <div className="flex items-center gap-1 text-[11px]">
            <button
              data-testid="lang-de"
              onClick={() => setLang("de")}
              className={`px-2 py-1 rounded ${lang === "de" ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}
            >DE</button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 rounded ${lang === "en" ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}
            >EN</button>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-xs text-slate-400 hover:text-white transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            {t.auth.logout}
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 lg:ml-64 min-w-0 pb-20 lg:pb-0">
        {presenting && (
          <div className="sticky top-0 z-20 bg-ag-ink text-amber-200 text-xs px-6 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2"><Sparkles className="h-3 w-3" /> Präsentationsmodus aktiv</div>
            <Button
              size="sm"
              variant="ghost"
              data-testid="stop-presentation-top"
              onClick={() => setPresenting(false)}
              className="text-amber-200 hover:text-white hover:bg-white/10 h-7"
            >Beenden</Button>
          </div>
        )}
        <main className="px-5 md:px-10 lg:px-14 py-8 lg:py-12 max-w-[1400px] mx-auto">
          {children}
          <AppFooter />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-ag-ink/95 backdrop-blur border-t border-white/10 px-2 py-2">
        <div className="flex items-center justify-around">
          {links.slice(0, 5).map(({ to, icon: Icon, key, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${testId}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-[10px] transition-colors ${
                  isActive ? "text-amber-300" : "text-slate-400"
                }`
              }
            >
              <Icon className="h-5 w-5" strokeWidth={1.5} />
              <span>{t.nav[key]}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
